<template>
  <div class="terminate" :class="{ complete: !showBtn }">
    <van-nav-bar :fixed="true" z-index="9" :title="title" :border="false" left-arrow @click-left="onClickLeft" />
    <van-pull-refresh
      class="detail-pull-refresh"
      v-model="refreshing"
      :pulling-text="$t('loading')"
      :loosing-text="$t('loading')"
      :loading-text="$t('loading')"
      success-text=""
      :success-duration="0"
      @refresh="onRefresh"
    >
      <div class="android-state-card" v-if="!orderId">
        <van-icon name="warning-o" />
        <span>{{ $t('orderPage.orderFailed') }}</span>
      </div>
      <div class="confirm android-card" v-if="orderDetail && breachStatus === BREACH_STATUS.NORMAL">
        <div class="confirm__icon"><van-icon name="warning" /></div>
        <div class="confirm__content">
          <div class="confirm__title">{{ $t('terminate.confirmCont') }}</div>
          <div class="confirm__desc">{{ $t('terminate.tips') }}</div>
        </div>
      </div>

      <div class="dwrap android-card" v-if="orderDetail">
      <div class="dwrap__title">{{ $t('terminate.orderInfo') }}</div>
      <div class="order-no-row" @click="copyOrderNumber">
        <span>{{ $t('orderList.orderNo', { id: orderDetail.orderId || orderId }) }}</span>
        <button type="button" class="order-copy-button" :aria-label="$t('orderPage.copyOrderNumber')" @click.stop="copyOrderNumber">
          <span class="order-copy-icon"></span>
        </button>
      </div>
      <div class="dwrap__body flex align-center order-info">
        <div class="order-info-img" v-lazy:background-image="orderDetail.goodsPic"></div>
        <div class="flex-1">
          <div class="text-ellipsis">{{ orderDetail.goodsName || '--' }}</div>
          <div>{{ skuStr }}</div>
        </div>
      </div>
      <div class="metrics-row">
        <div>
          <span>{{ $t('terminate.deposit') }}</span>
          <strong>{{ formatRupiah(orderDetail.depositAmount) }}</strong>
        </div>
        <div>
          <span>{{ $t('orderPage.firstRentalAmount') }}</span>
          <strong>{{ formatRupiah(firstRentalAmount) }}</strong>
        </div>
      </div>
      </div>

      <div class="dwrap android-card">
      <div class="dwrap__title dwrap__title--inline">
        <span>{{ $t('terminate.paymentDue') }}</span>
        <van-icon name="question-o" @click="showPaymentDueRule" />
      </div>
      <div class="dwrap__body payment-info">
        <div>{{ $t('terminate.penaltyFee') }}</div>
        <div>{{ priceUint }}{{ paymentDue | numberFormat }}</div>
      </div>
      <div class="payment-hint">{{ $t('terminate.penaltyFeeHint') }}</div>
      <div class="paid-row" v-if="showPaidInfo">
        <div><van-icon name="checked" />{{ $t('terminate.paid') }}</div>
        <span v-if="paidTimeText">{{ $t('terminate.paidTime', { time: paidTimeText }) }}</span>
      </div>
      </div>

      <div class="express android-card" v-if="showExpress">
      <div class="express__title">{{ $t('terminate.express') }}</div>
      <div class="express-desc">{{ $t('terminate.returnShippingDesc') }}</div>
      <div class="express-input">
        <div class="tracking-label">{{ $t('terminate.expressNumber') }}</div>
        <input type="text" class="tracking-input" :placeholder="$t('terminate.trackingPlaceholder')" v-model="expressNumber"
          maxlength="60">
        <div class="carrier-row" @click="openPicker" :class="{ select: !selectedCarrierName }">
          <span>{{ selectedCarrierName || $t('terminate.selectShippingCompany') }}</span>
          <van-icon name="arrow" />
        </div>
        <div class="btn-primary shipping-submit" :class="{ disabled: submitting }" @click="submitExpress">{{ $t('terminate.btnExpress') }}</div>
      </div>
      </div>

      <div class="dwrap android-card" v-if="showProcessNodes">
      <div class="dwrap__title">{{ $t('terminate.progress') }}</div>
      <div class="dwrap__body progress-info">
        <div class="flex progress-info__item" :class="{ ac: index === 0 }" v-for="(nodes, index) in processNodes" :key="index">
          <div class="dot"></div>
          <div class="mg-t-10 flex-1">
            <div class="item-text1">{{ nodes.completedTimeText }}</div>
            <div class="item-text2 t-break" v-html="nodes.desc"></div>
          </div>
        </div>
      </div>
      </div>

      <div class="terminate__notes android-card">
      <div class="dwrap__title">{{ $t('terminate.importantNotes') }}</div>
      <div class="notice-row">
        <div class="notice-row__icon notice-row__icon--blue"><van-icon name="description" /></div>
        <div class="notice-row__content">
          <div class="notes-title">{{ $t('terminate.earlyTerminationTitle') }}</div>
          <div class="notes-content">
            <p>{{ $t('terminate.earlyTerminationDesc') }}</p>
            <p>{{ $t('terminate.earlyTerminationDesc2') }}</p>
          </div>
        </div>
      </div>
      <div class="notice-divider"></div>
      <div class="notice-row">
        <div class="notice-row__icon notice-row__icon--green"><van-icon name="shield-o" /></div>
        <div class="notice-row__content">
          <div class="notes-title">{{ $t('terminate.depositNoticeTitle') }}</div>
          <div class="notes-content">{{ $t('terminate.depositNoticeDesc') }}</div>
        </div>
      </div>
      </div>
    </van-pull-refresh>
    <div class="terminate__footer" v-if="showBtn">
      <div class="btn-primary" :class="{ disabled: submitting, 'btn-primary--pay': breachStatus === BREACH_STATUS.APPROVED }" @click="onBtnClick">{{ btnText }}</div>
    </div>
    <picker ref="picker" :columns="buildCarriers" @confirm="onPickerConfirm"></picker>
    <Popup v-model="showPayUrlPopup" position="bottom">
      <div class="pay-url-popup">
        <div class="pay-url-popup__close" @click="showPayUrlPopup = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="pay-url-popup__title">{{ $t('orderPage.payUrlTitle') }}</div>
        <div class="pay-url-popup__desc">{{ $t('orderPage.payUrlDesc') }}</div>
        <div class="pay-url-popup__footer">
          <div class="btn-primary btn-normal" @click="goToPayUrl">{{ $t('orderPage.goToPay') }}</div>
          <div
            class="pay-url-popup__completed-btn"
            :class="{ 'pay-url-popup__completed-btn--disabled': checkingPaymentStatus }"
            @click="checkBreachPaymentStatus"
          >
            {{ $t('orderPage.paymentCompletedButton') }}
          </div>
        </div>
      </div>
    </Popup>
    <PaymentPopup ref="paymentPopup"/>
  </div>
</template>
<script>
import { Dialog, Icon, Popup, PullRefresh } from 'vant';
import picker from '@/components/picker.vue';
import PaymentPopup from '@/components/PaymentPopup/index.vue';
import { queryOrderDetail } from '@/api/order';
import { queryPaymentInfo } from '@/api/payment';
import { queryBreachOrder, applyBreach, payBreach, expressBreach } from '@/api/breach';
import {BREACH_STATUS, SHIPPING_COMPANY, PAYMENT_STATUS} from '@/common/constants';
import routerBack from '@/mixins/routerBack';
import {queryCarriers} from "@/api/common";
import { formatRentalDateTime } from '@/filters';
import { copyText } from '@/utils/clipboard';
import { formatRupiahAmount } from '@/utils/money';
export default {
  name: 'Terminate',
  mixins: [routerBack],
  components: {
    picker,
    'van-icon': Icon,
    'van-pull-refresh': PullRefresh,
    Popup,
    PaymentPopup,
  },

  data() {
    return {
      BREACH_STATUS,
      refreshing: false,
      isLoad: false,
      orderId: null,
      orderDetail: null,
      berachDetail: null,
      expressNumber: '',
      expressCompany: null,
      pickerData: SHIPPING_COMPANY,
      penaltyFee: 0,
      carriers:[],
      showPayUrlPopup: false,
      payUrl: '',
      checkingPaymentStatus: false,
      submitting: false,
    }
  },
  computed: {
    firstRentalAmount() {
      return [this.orderDetail?.planPrice, this.orderDetail?.riskAmount]
        .reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    title() {
      return this.breachStatus === BREACH_STATUS.NORMAL ? this.$t('terminate.title') : this.$t('terminate.detailsTitle');
    },
    priceUint() {
      return this.$t('priceUnit');
    },
    formatRupiah() {
      return amount => {
        const value = Number(amount || 0);
        return `${this.priceUint}${formatRupiahAmount(Math.abs(value)) || '0'}`;
      };
    },
    skuStr() {
      if (!this.orderDetail) {
        return '';
      }
      let { skuInfo, planIndex, planUnit } = this.orderDetail;
      const cycle = planUnit && this.$te && this.$te(`cycle.${planUnit}`) ? this.$t(`cycle.${planUnit}`) : planUnit;
      return [skuInfo, planIndex, cycle].filter(item => item !== undefined && item !== null && String(item).trim()).join(' · ') || '--';
    },
    paymentDue() {
      return this.berachDetail && this.berachDetail.status !== BREACH_STATUS.REJECT ? this.berachDetail.penaltyFee || 0 : this.penaltyFee || 0;
    },
    showPaidInfo() {
      return [
        BREACH_STATUS.PAID,
        BREACH_STATUS.SUBMIT_TRACKING,
        BREACH_STATUS.ACCEPT_TRACKING,
        BREACH_STATUS.CHECKED_DEVICE,
        BREACH_STATUS.DEPOSIT_REFUND,
        BREACH_STATUS.COMPLETED,
      ].includes(this.breachStatus) || this.displayPaidTime > 0;
    },
    displayPaidTime() {
      if (this.berachDetail?.paidTime > 0) return this.berachDetail.paidTime;
      const paidNode = (this.berachDetail?.processNodes || []).find(item => String(item.nodeType || '').toUpperCase() === 'PAID');
      return paidNode?.completedTime || 0;
    },
    paidTimeText() {
      if (!this.displayPaidTime) return '';
      return this.formatDateTimeWithMeridiem(this.displayPaidTime);
    },
    selectedCarrierName() {
      if (this.expressCompany?.text) return this.expressCompany.text;
      const trackingCompanyKey = this.berachDetail?.trackingCompanyKey;
      const trackingCompany = this.berachDetail?.trackingCompany;
      return trackingCompany || this.buildCarriers.find(item => String(item.value) === String(trackingCompanyKey))?.text || '';
    },
    showProcessNodes() {
      return this.processNodes.length > 0;
    },
    showExpress() {
      return this.breachStatus === BREACH_STATUS.PAID && !this.berachDetail?.trackingNo;
    },
    showBtn() {
      return [BREACH_STATUS.NORMAL, BREACH_STATUS.REJECT, BREACH_STATUS.APPROVED].includes(this.breachStatus);
    },
    // eslint-disable-next-line vue/return-in-computed-property
    btnText() {
      switch (this.breachStatus) {
        case BREACH_STATUS.NORMAL:
          return this.$t('terminate.btnConfirm');
        case BREACH_STATUS.REJECT:
          return this.$t('terminate.btnReapply');
        case BREACH_STATUS.APPROVED:
          return this.$t('terminate.btnPay');
      }
    },
    breachStatus() {
      if (this.isLoad && !this.berachDetail) {
        return BREACH_STATUS.NORMAL;
      }
      const status = String(this.berachDetail?.status || '').toUpperCase();
      if (!status) return BREACH_STATUS.NORMAL;
      if (status === BREACH_STATUS.APPROVED) {
        if (this.berachDetail?.trackingNo) return BREACH_STATUS.SUBMIT_TRACKING;
        if (Number(this.berachDetail?.paidTime || 0) > 0 || Number(this.berachDetail?.penaltyFee || 0) <= 0) return BREACH_STATUS.PAID;
      }
      return Object.values(BREACH_STATUS).includes(status) ? status : BREACH_STATUS.SUBMIT;
    },
    processNodes() {
      return (this.berachDetail?.processNodes || []).map((item) => {
        // 处理物流信息
        if (item.nodeType === 'SUBMIT_TRACKING' && item.extInfo) {
          try {
            let extInfo = JSON.parse(item.extInfo);
            let trackingInfo = this.buildCarriers.find(item => String(item.value) === String(extInfo.trackingCompany)) || {};
            // 后端加了空格返回必须加空格{ xxx }
            item.desc = item.desc.replace(`{ ${extInfo.trackingCompany} }`, trackingInfo.text || extInfo.trackingCompany);
          } catch (e) {
            // extInfo 异常时保留后端原始进度文案，避免进度卡整体渲染失败。
          }
        }
        return {
          ...item,
          completedTimeText: this.formatDateTimeWithMeridiem(item.completedTime),
        }
      });
    },
    buildCarriers() {
      return this.carriers.map(item => {
        return {
          value: item.key,
          text: item.name,
          sort: item.sort,
        }
      }).sort((a, b) => a.sort - b.sort)
    }
  },
  mounted() {
    this.orderId = this.$route.query.orderId;
    if (this.orderId) {
      this.getBreachOrder();
      this.getOrderDetail();
      this.penaltyFee = this.$route.query.penaltyFee || 0;
      //查询物流公司
      this.doQueryCarriers();
    }
  },
  methods: {
    async onRefresh() {
      if (!this.orderId || this.submitting) {
        this.refreshing = false;
        return;
      }
      try {
        await Promise.all([
          this.getBreachOrder(),
          this.getOrderDetail(),
          this.doQueryCarriers(),
        ]);
      } finally {
        this.refreshing = false;
      }
    },
    getOrderDetail() {
      return queryOrderDetail({
        orderId: this.orderId
      }).then(res => {
        if (res.code === 'Err.ErrOrderIllegal') {
          this.$toast(res.message);
          setTimeout(() => {
            this.$router.replace({ name: 'Index' });
          }, 3000);
          return;
        }
        if (res.code === 'Success') {
          this.orderDetail = res.data;
        }
        return res;
      })
    },
    getBreachOrder() {
      return queryBreachOrder({
        rentalOrderId: this.orderId
      }).then(res => {
        this.isLoad = true;
        if (res.code === 'Success') {
          this.berachDetail = res.data;
        }
        return res;
      })
    },
    doApplyBreach() {
      if (this.submitting || !this.orderId) return;
      this.submitting = true;
      applyBreach({
        rentalOrderId: this.orderId,
      }).then(res => {
        if (res.code === 'Success') {
          this.$toast(this.$t('terminate.applySuccess'));
          this.getBreachOrder();
        } else {
          this.$toast(res.message || this.$t('systemError'));
        }
      }).catch(() => {
        this.$toast(this.$t('systemError'));
      }).finally(() => {
        this.submitting = false;
      })
    },
    onBtnClick() {
      if (this.submitting) return;
      if ([BREACH_STATUS.NORMAL, BREACH_STATUS.REJECT].includes(this.breachStatus)) {
        Dialog.confirm({
          title: this.$t('terminate.submitConfirmTitle'),
          message: this.$t('terminate.submitConfirmDesc'),
          confirmButtonText: this.btnText,
          cancelButtonText: this.$t('cancel'),
          confirmButtonColor: '#FF2D20',
        }).then(() => {
          this.doApplyBreach();
        }).catch(() => {});
      } else if (this.breachStatus === BREACH_STATUS.APPROVED) {
        this.goToBreachCheckout();
      }
    },
    onClickLeft() {
      this.goBack()
    },
    openPicker() {
      this.$refs.picker.open();
    },
    onPickerConfirm(data) {
      this.expressCompany = data;
    },
    formatDateTimeWithMeridiem(date) {
      return formatRentalDateTime(date, '');
    },
    showPaymentDueRule() {
      Dialog.alert({
        title: this.$t('terminate.paymentDueRuleTitle'),
        message: this.$t('terminate.paymentDueRuleDesc'),
        confirmButtonText: this.$t('confirm'),
        confirmButtonColor: '#0B45FF',
      });
    },
    async copyOrderNumber() {
      const orderNumber = this.orderDetail?.orderId || this.orderId;
      if (!orderNumber) return;
      const success = await copyText(orderNumber);
      this.$toast({
        message: success ? this.$t('orderPage.copySuccess') : this.$t('orderPage.copyFailed'),
        duration: 1500
      });
    },
    goToBreachCheckout() {
      if (this.submitting || !this.orderId) return;
      this.$router.push({
        name: 'BreachCheckout',
        query: { orderId: this.orderId }
      });
    },
    doPay() {
      if (this.submitting || !this.berachDetail?.breachOrderId) return;
      this.submitting = true;
      payBreach({
        breachOrderId: this.berachDetail.breachOrderId,
        returnUrl: location.pathname + location.search,
      }).then(res => {
        if (res?.code === 'Success' && res.data?.payContent) {
          this.$refs.paymentPopup.open({
            payContent: res.data.payContent,
            amount: res.data.amount || this.paymentDue,
          });
        } else if (res?.code === 'Success' && res.data?.payUrl) {
          this.payUrl = res.data.payUrl;
          this.showPayUrlPopup = true;
        } else {
          this.$toast(res.message || this.$t('systemError'));
        }
      }).catch(() => {
        this.$toast(this.$t('systemError'));
      }).finally(() => {
        this.submitting = false;
      })
    },
    goToPayUrl() {
      if (!this.payUrl) {
        return;
      }
      window.open(this.payUrl, '_blank', 'noopener,noreferrer');
    },
    async checkBreachPaymentStatus() {
      const breachOrderId = this.berachDetail?.breachOrderId;
      if (this.checkingPaymentStatus || !breachOrderId) {
        return;
      }
      this.checkingPaymentStatus = true;
      try {
        const res = await queryPaymentInfo({ orderId: breachOrderId });
        if (res?.code !== 'Success') {
          this.$toast(res?.message || this.$t('systemError'));
          return;
        }
        if (res.data?.state !== PAYMENT_STATUS.SUCCESS) {
          this.$toast(this.$t('orderPage.paymentNotCompleted'));
          return;
        }

        this.showPayUrlPopup = false;
        await Promise.all([
          this.getBreachOrder(),
          this.getOrderDetail(),
        ]);
      } catch (e) {
        this.$toast(this.$t('systemError'));
      } finally {
        this.checkingPaymentStatus = false;
      }
    },
    submitExpress() {
      if (this.submitting) return;
      if (!this.expressNumber) {
        this.$toast(this.$t('terminate.expressNumberTip'));
        return;
      }
      const carrierKey = this.expressCompany?.value || this.berachDetail?.trackingCompanyKey;
      if (!carrierKey || Number.isNaN(Number(carrierKey))) {
        this.$toast(this.$t('terminate.expressCompanyTip'));
        return;
      }
      this.submitting = true;
      expressBreach({
        breachOrderId: this.berachDetail.breachOrderId,
        trackingNo: this.expressNumber,
        trackingCompanyKey: Number(carrierKey)
      }).then(res => {
        if (res?.code === 'Success') {
          this.$toast(this.$t('terminate.expressSuccess'));
          this.getBreachOrder();
        } else {
          this.$toast(res.message || this.$t('systemError'));
        }
      }).catch(() => {
        this.$toast(this.$t('systemError'));
      }).finally(() => {
        this.submitting = false;
      })
    },
    doQueryCarriers() {
      return queryCarriers().then(res => {
        if (res?.code === 'Success') {
          this.carriers = res.data;
        }
      })
    },
  },
}
</script>
<style lang="less">
.notes-title {
  font-size: 30px;
  line-height: 40px;
  font-weight: 500;
}
.notes-content {
  font-size: 26px;
  line-height: 34px;
  margin-top: 10px;
  color: var(--text-color-gray);
}
.notes-content p {
  margin-top: 24px;
}
.notes-content p:first-child {
  margin-top: 0;
}

.pay-url-popup {
  position: relative;
  padding: 48px 40px 44px;
  box-sizing: border-box;
  text-align: center;

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 36px;
    height: 36px;

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__title {
    font-size: 34px;
    line-height: 48px;
    font-weight: 600;
    color: var(--text-color-black);
  }

  &__desc {
    margin-top: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);
  }

  &__footer {
    margin-top: 40px;
  }

  &__completed-btn {
    height: 80px;
    margin-top: 20px;
    border: 2px solid var(--primary-color);
    border-radius: var(--border-radius-base);
    box-sizing: border-box;
    font-size: 28px;
    line-height: 76px;
    color: var(--primary-color);

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>
<style lang="less" scoped>
.terminate {
  padding-top: 88px;
  min-height: 100vh;
  background: #F3F7FF;
  padding-bottom: 164px;

  &__notes {
    margin: 24px 24px 0;
    padding: 24px;
  }

  &__footer {
    position: fixed;
    padding: 16px 24px calc(16px + env(safe-area-inset-bottom));
    left: 0;
    bottom: 0;
    width: 100%;
    box-sizing: border-box;
    background: #F3F7FF;
    z-index: 99;

    .btn-primary {
      height: 88px;
      border-radius: 16px;
      line-height: 88px;
      background: #FF2D20;
      font-size: 30px;
      font-weight: 400;

      &--pay {
        background: #0B45FF;
      }
    }
  }

  &.complete {
    padding-bottom: 30px;

    .terminate__footer {
      display: none;
    }
  }
}

.confirm {
  display: flex;
  margin: 24px 24px 0;
  padding: 24px;
  background: #EAF3FF;
  border: 0;

  &__title {
    color: #070D3F;
    font-size: 28px;
    line-height: 38px;
    font-weight: 500;
  }

  &__desc {
    margin-top: 4px;
    color: #64748B;
    font-size: 26px;
    line-height: 36px;
  }

  &__icon {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    margin-right: 20px;
    border-radius: 18px;
    color: #FFFFFF;
    background: #FF7A00;
    text-align: center;
    line-height: 48px;
    font-size: 30px;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }
}

.android-card {
  box-sizing: border-box;
  border: 2px solid #E5EAF3;
  border-radius: 16px;
  background: #FFFFFF;
}

.android-state-card {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 24px;
  padding: 28px 24px;
  box-sizing: border-box;
  border: 2px solid #E5EAF3;
  border-radius: 16px;
  color: #64748B;
  background: #FFFFFF;
  font-size: 26px;
  line-height: 36px;

  .van-icon {
    color: #FF7A00;
    font-size: 34px;
  }
}

.btn-primary.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.dwrap {
  padding: 24px;
  margin: 24px 24px 0;
  overflow: hidden;

  &__title {
    display: flex;
    align-items: center;
    padding-bottom: 20px;
    border-bottom: 2px solid #E5EAF3;
    color: #070D3F;
    font-size: 32px;
    line-height: 44px;
    font-weight: 500;

    &--inline {
      gap: 10px;

      .van-icon {
        color: #94A3B8;
        font-size: 30px;
      }
    }
  }

  &__body {
    margin-top: 20px;
    padding: 0;
  }
}

.order-no-row {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
  color: #64748B;
  font-size: 24px;
  line-height: 34px;
  cursor: pointer;

  > span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.order-copy-button {
  position: relative;
  flex: 0 0 36px;
  width: 36px;
  height: 38px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.order-copy-icon,
.order-copy-icon::before {
  position: absolute;
  box-sizing: border-box;
  width: 22px;
  height: 28px;
  border: 3px solid #0B45FF;
  border-radius: 4px;
  background: #FFFFFF;
  content: '';
}

.order-copy-icon {
  left: 4px;
  bottom: 3px;
}

.order-copy-icon::before {
  top: -8px;
  left: 8px;
}

.order-info {
  margin-top: 28px;

  .flex-1 {
    flex: 1;
    padding-left: 20px;
    min-width: 0;
    font-size: 26px;
    color: #64748B;
    line-height: 36px;

    >div {
      margin-top: 8px;

      &:first-child {
        color: #070D3F;
        font-weight: 500;
        width: auto;
      }
    }
  }
}

.order-info-img {
  width: 120px;
  height: 120px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
  border: 2px solid #E5EAF3;
  overflow: hidden;
}

.metrics-row {
  display: flex;
  gap: 24px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 2px solid #E5EAF3;

  > div {
    flex: 1;
    min-width: 0;
  }

  span,
  strong {
    display: block;
    font-size: 26px;
    line-height: 36px;
    font-weight: 400;
  }

  span {
    color: #64748B;
  }

  strong {
    margin-top: 4px;
    color: #0B45FF;
  }
}

.payment-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 28px;
  color: #070D3F;
  line-height: 38px;

  > div:first-child {
    font-weight: 500;
  }

  > div:last-child {
    color: #FF7A00;
  }
}

.payment-hint {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 2px solid #E5EAF3;
  color: #64748B;
  font-size: 24px;
  line-height: 34px;
}

.paid-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 2px solid #E5EAF3;
  color: #64748B;
  font-size: 24px;
  line-height: 34px;

  > div {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #16A34A;
  }

  > span {
    margin-left: auto;
    text-align: right;
  }
}

.progress-info {
  &__item {
    padding-bottom: 30px;
    position: relative;

    &::after {
      position: absolute;
      content: '';
      top: 36px;
      left: 10px;
      width: 0;
      height: calc(100% - 48px);
      border-left: 2px solid #D8D8D8;
      z-index: 1;
    }

    &:last-child {
      padding-bottom: 0;

      &::after {
        display: none;
      }
    }

    .dot {
      position: relative;
      z-index: 2;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #FFFFFF;
      border: 2px solid #999999;
    }

    .flex-1 {
      padding-left: 14px;
      font-size: 26px;
      color: var(--text-color-gray);
      line-height: 30px;
    }

    .item-text2 {
      margin-top: 12px;
    }

    &.ac {
      .dot {
        background: var(--primary-color);
      }

      .item-text1 {
        color: var(--primary-color);
      }

      .item-text2 {
        color: var(--text-color-black);
      }
    }

  }
}

.express {
  margin: 24px 24px 0;
  padding: 24px;

  &__title {
    padding-bottom: 20px;
    border-bottom: 2px solid #E5EAF3;
    color: #070D3F;
    font-size: 32px;
    line-height: 44px;
    font-weight: 500;
  }

  &-input {
    margin-top: 20px;
  }
}

.express-desc {
  margin-top: 20px;
  color: #64748B;
  font-size: 24px;
  line-height: 34px;
}

.tracking-label {
  margin-top: 24px;
  color: #64748B;
  font-size: 24px;
  line-height: 34px;
}

.tracking-input,
.carrier-row {
  box-sizing: border-box;
  width: 100%;
  height: 72px;
  margin-top: 12px;
  padding: 0 24px;
  border: 2px solid #DDE5F2;
  border-radius: 20px;
  color: #070D3F;
  font-size: 36px;
  line-height: 68px;
  background: #FFFFFF;
}

.carrier-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.select {
  color: #64748B;
}

.shipping-submit {
  height: 80px;
  margin-top: 24px;
  border-radius: 16px;
  line-height: 80px;
}

.notice-row {
  display: flex;
  align-items: flex-start;
  margin-top: 20px;

  &__icon {
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    margin-right: 24px;
    border-radius: 16px;
    text-align: center;
    line-height: 64px;
    font-size: 34px;

    &--blue {
      color: #0B45FF;
      background: #EAF3FF;
    }

    &--green {
      color: #16A34A;
      background: #EAFBF0;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }
}

.notice-divider {
  height: 2px;
  margin: 24px 0;
  background: #E5EAF3;
}

.detail-pull-refresh::v-deep .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}
</style>
